import React, { FC, useEffect } from 'react';

import { Box, HStack, Stack, useDisclosure } from '@chakra-ui/react';

import { DetailsIdentifier, timeLabel } from 'components/DataDisplay';
import { CVEReferencesModal } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/CVEReferencesModal';
import {
  renderCVEReferences,
  renderCVSSScoreTag,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/utils';

interface Props {
  data: Record<string, any>;
  horizontalSpacing?: number;
  verticalSpacing?: number;
  headerFontSize?: number | string;
}

export const CVEAdditionalDetails: FC<Props> = props => {
  const {
    data,
    verticalSpacing = 3,
    horizontalSpacing = 6,
    headerFontSize,
  } = props;
  const showMoreLinks = useDisclosure();
  const [cvssVector, setCvssVector] = React.useState<Record<string, any>>({});

  useEffect(() => {
    setCvssVector(data?.cvss_v3 ?? data?.cvss_v3_vector);
  }, [data]);

  return (
    <Box>
      <DetailsIdentifier
        label="Additional Details"
        spacing={4}
        isInline={false}
        styles={{ label: { fontWeight: 600, fontSize: headerFontSize } }}
        value={
          <HStack spacing={horizontalSpacing}>
            <Stack w="60%" spacing={verticalSpacing}>
              <Box>
                <DetailsIdentifier
                  label="CVSS V3 Vector"
                  value={cvssVector?.vector ?? 'None'}
                />
              </Box>
              <Box>
                <DetailsIdentifier
                  label="Impact Score"
                  value={
                    cvssVector?.impact_score
                      ? renderCVSSScoreTag(cvssVector?.impact_score)
                      : 'None'
                  }
                />
              </Box>
              <Box>
                <DetailsIdentifier
                  label="Attack Vector (AV)"
                  value={cvssVector?.attack_vector ?? 'None'}
                />
              </Box>
              <Box>
                <DetailsIdentifier
                  label="Attack Complexity (AC)"
                  value={cvssVector?.attack_complexity ?? 'None'}
                />
              </Box>
              <Box>
                <DetailsIdentifier
                  label="Privileges Required (PR)"
                  value={cvssVector?.privileges_required ?? 'None'}
                />
              </Box>
              <Box>
                <DetailsIdentifier
                  label="User Interaction (UI)"
                  value={cvssVector?.user_interaction ?? 'None'}
                />
              </Box>
            </Stack>
            <Stack w="40%" spacing={verticalSpacing}>
              <Box>
                <DetailsIdentifier
                  label="Published"
                  value={
                    data?.published_date
                      ? timeLabel(data?.published_date)
                      : 'None'
                  }
                />
              </Box>
              <Box>
                <DetailsIdentifier
                  label="Additional Sources"
                  value={
                    <>
                      {renderCVEReferences(data, showMoreLinks.onOpen)}
                      {showMoreLinks.isOpen && (
                        <CVEReferencesModal
                          cveDetails={data}
                          isOpen={showMoreLinks.isOpen}
                          onClose={showMoreLinks.onClose}
                        />
                      )}
                    </>
                  }
                />
              </Box>
              <Box>
                <DetailsIdentifier
                  label="Exploitability Score"
                  value={
                    cvssVector?.exploitability_score
                      ? renderCVSSScoreTag(cvssVector?.exploitability_score)
                      : 'None'
                  }
                />
              </Box>
              <Box>
                <DetailsIdentifier
                  label="Confidentiality Impact (C)"
                  value={cvssVector?.confidentiality_impact ?? 'None'}
                />
              </Box>
              <Box>
                <DetailsIdentifier
                  label="Integrity Impact (I)"
                  value={cvssVector?.integrity_impact ?? 'None'}
                />
              </Box>
              <Box>
                <DetailsIdentifier
                  label="Availability Impact (A)"
                  value={cvssVector?.availability_impact ?? 'None'}
                />
              </Box>
            </Stack>
          </HStack>
        }
      />
    </Box>
  );
};
